import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import SchoolIcon from "@mui/icons-material/School";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import {
  getApplicantConfig,
  saveApplicantConfig,
  institutionOptions,
  openInstitutionOptions,
  openLevelsOf,
  academicLevelsOf,
  FIXED_OPEN_LEVELS,
  allProfileDocs,
  PROFILE_SECTION_LABELS,
  DEFAULT_APPLICANT_CONFIG,
} from "../../../services/backend/ApplicantConfigService";
import { parseInstitutionsCsv } from "./institutionsCsv";
import LinesField from "./LinesField";

// Admin-only configuration for the Applicants module — the single source of
// truth (config/applicants) for which documents may be uploaded, which are
// required, how many of each, the ID types / academic levels, the max file size,
// the storage bucket, and the Document AI (OCR) processor. Gated on the ADMIN
// role (mirrors PqrsdConfig's isFullAdmin gate).

const blankDoc = () => ({
  key: "",
  label: "",
  stage: 1,
  required: true,
  enabled: true,
  minFiles: 1,
  maxFiles: 1,
  order: 0,
  acceptedMime: ["application/pdf", "image/*"],
  description: "",
  aiHints: "",
  keywords: [],
  extra: {},
});

// Comma-separated <-> array helpers for the simple list fields.
const toList = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
// Stable id from a call name (lowercase, ascii, dash-separated).
const slugify = (s) =>
  String(s || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const blankApplication = () => ({ id: "", name: "", active: true, order: 0, requiredDocsStage1: [] });

export default function ApplicantConfig() {
  const user = useSelector((state) => state.user.value);
  const isAdmin = !!(user?.roles || []).includes("ADMIN");

  const [cfg, setCfg] = useState(DEFAULT_APPLICANT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [tab, setTab] = useState("profileDocuments");
  const [profileDialog, setProfileDialog] = useState(null); // { mode, origKey, doc }
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', origKey, doc }
  const [postDialog, setPostDialog] = useState(null); // { mode, index, post }

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setCfg(await getApplicantConfig());
      setLoading(false);
    })();
  }, [isAdmin]);

  const catalog = useMemo(
    () => (cfg.documentCatalog || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [cfg.documentCatalog]
  );

  // Convocatorias, General first then by order.
  const applications = useMemo(
    () => (cfg.applications || []).slice().sort(
      (a, b) => (a.id === "general" ? -1 : b.id === "general" ? 1 : (a.order || 0) - (b.order || 0))
    ),
    [cfg.applications]
  );
  // Stage-1 catalog keys — the pool a call draws its required docs from.
  const stage1Docs = useMemo(
    () => (cfg.documentCatalog || []).filter((d) => d && Number(d.stage || 1) === 1),
    [cfg.documentCatalog]
  );

  // Catálogo de perfil (documentos de la persona). Los atados a formación /
  // experiencia son protegidos: se pueden renombrar, volver opcionales o apagar
  // —apagarlos oculta su sección completa— pero no borrar ni re-apuntar.
  const profileCatalog = useMemo(() => allProfileDocs(cfg), [cfg]);

  const persist = async (patch) => {
    setSaving(true);
    const ok = await saveApplicantConfig(patch);
    setSaving(false);
    setFeedback(ok
      ? { severity: "success", msg: "Configuración guardada." }
      : { severity: "error", msg: "No fue posible guardar la configuración." });
    return ok;
  };

  const saveCatalog = (nextCatalog) => {
    setCfg((c) => ({ ...c, documentCatalog: nextCatalog }));
    return persist({ documentCatalog: nextCatalog });
  };

  const handleSaveDoc = async (doc) => {
    // Uniqueness on key — the doc being edited is matched by its original key,
    // never by list position (the cards render a sorted copy of the catalog).
    const key = (doc.key || "").trim();
    if (!key) { setFeedback({ severity: "warning", msg: "La clave (key) es obligatoria." }); return; }
    const list = cfg.documentCatalog || [];
    const origKey = dialog.mode === "edit" ? dialog.origKey : null;
    if (list.some((d) => d.key === key && d.key !== origKey)) {
      setFeedback({ severity: "warning", msg: `Ya existe un documento con la clave "${key}".` }); return;
    }
    const clean = { ...doc, key };
    const next = dialog.mode === "create"
      ? [...list, clean]
      : list.map((d) => (d.key === origKey ? clean : d));
    if (await saveCatalog(next)) setDialog(null);
  };

  const handleDeleteDoc = async (docKey) => {
    if (!window.confirm(`¿Eliminar el documento "${docKey}"?`)) return;
    await saveCatalog((cfg.documentCatalog || []).filter((d) => d.key !== docKey));
  };

  const saveProfileCatalog = (next) => {
    setCfg((c) => ({ ...c, profileDocumentCatalog: next }));
    return persist({ profileDocumentCatalog: next });
  };

  const handleSaveProfileDoc = async (doc) => {
    const key = (doc.key || "").trim();
    if (!key) { setFeedback({ severity: "warning", msg: "La clave (key) es obligatoria." }); return; }
    const list = profileCatalog;
    const origKey = profileDialog.mode === "edit" ? profileDialog.origKey : null;
    if (list.some((d) => d.key === key && d.key !== origKey)) {
      setFeedback({ severity: "warning", msg: `Ya existe un documento de perfil con la clave "${key}".` }); return;
    }
    const clean = { ...doc, key };
    const next = profileDialog.mode === "create"
      ? [...list, clean]
      : list.map((d) => (d.key === origKey ? clean : d));
    if (await saveProfileCatalog(next)) setProfileDialog(null);
  };

  const handleDeleteProfileDoc = async (doc) => {
    if (doc.protected) {
      setFeedback({
        severity: "warning",
        msg: "Este documento está atado a una sección del perfil y no se puede eliminar. Desactívalo si no quieres pedirlo.",
      });
      return;
    }
    if (!window.confirm(`¿Eliminar el documento de perfil "${doc.key}"?`)) return;
    await saveProfileCatalog(profileCatalog.filter((d) => d.key !== doc.key));
  };

  // Apagar/encender un documento atado apaga/enciende su sección completa.
  const toggleProfileDoc = async (doc, enabled) => {
    await saveProfileCatalog(profileCatalog.map((d) => (d.key === doc.key ? { ...d, enabled } : d)));
  };

  // --- Convocatorias -------------------------------------------------------
  const saveApplications = (next) => {
    setCfg((c) => ({ ...c, applications: next }));
    return persist({ applications: next });
  };

  const handleSaveApplication = async (post) => {
    const name = (post.name || "").trim();
    if (!name) { setFeedback({ severity: "warning", msg: "El nombre es obligatorio." }); return; }
    const isEdit = postDialog.mode === "edit";
    // General keeps its id; new ones slug from the name (stable once created).
    const isGeneral = isEdit && post.id === "general";
    const id = isGeneral ? "general" : (isEdit && post.id) || slugify(name);
    if (!id) { setFeedback({ severity: "warning", msg: "El nombre debe contener letras o números." }); return; }
    const list = cfg.applications || [];
    if (list.some((p) => p.id === id && p.id !== (isEdit ? post.id : null))) {
      setFeedback({ severity: "warning", msg: `Ya existe una convocatoria con el id "${id}".` }); return;
    }
    const clean = { ...post, id, name, active: isGeneral ? true : post.active !== false };
    const next = isEdit
      ? list.map((p) => (p.id === post.id ? clean : p))
      : [...list, clean];
    if (await saveApplications(next)) setPostDialog(null);
  };

  const handleDeleteApplication = async (post) => {
    if (post.id === "general" || post.protected) {
      setFeedback({ severity: "warning", msg: "La convocatoria General no se puede eliminar." });
      return;
    }
    if (!window.confirm(`¿Eliminar la convocatoria "${post.name}"?`)) return;
    await saveApplications((cfg.applications || []).filter((p) => p.id !== post.id));
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Sin acceso</Typography>
        <Typography variant="body2" color="text.secondary">
          Sólo un administrador puede configurar el módulo de Aspirantes.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SchoolIcon color="primary" />
        <Typography variant="h4">Configuración de aspirantes</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define qué documentos pueden subir los aspirantes, cuáles son obligatorios y cuántos de cada
        uno, junto con los tipos de documento, niveles académicos y el almacenamiento.
      </Typography>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="profileDocuments" label="Documentos del aspirante" />
        <Tab value="documents" label="Documentos por convocatoria" />
        <Tab value="applications" label="Postulaciones" />
        <Tab value="listas" label="Listas" />
        <Tab value="instituciones" label="Instituciones" />
        <Tab value="almacenamiento" label="Almacenamiento" />
      </Tabs>

      {loading ? (
        <Typography>Cargando…</Typography>
      ) : (
        <>
          {tab === "profileDocuments" && (
            <ProfileDocumentsTab
              catalog={profileCatalog}
              onCreate={() => setProfileDialog({ mode: "create", doc: blankProfileDoc() })}
              onEdit={(doc) => setProfileDialog({ mode: "edit", origKey: doc.key, doc })}
              onDelete={handleDeleteProfileDoc}
              onToggle={toggleProfileDoc}
              saving={saving}
            />
          )}
          {tab === "documents" && (
            <DocumentsTab
              catalog={catalog}
              onCreate={() => setDialog({ mode: "create", doc: blankDoc() })}
              onEdit={(doc) => setDialog({ mode: "edit", origKey: doc.key, doc })}
              onDelete={handleDeleteDoc}
            />
          )}
          {tab === "applications" && (
            <ApplicationsTab
              applications={applications}
              stage1Docs={stage1Docs}
              onCreate={() => setPostDialog({ mode: "create", post: blankApplication() })}
              onEdit={(post) => setPostDialog({ mode: "edit", post })}
              onDelete={handleDeleteApplication}
            />
          )}
          {tab === "listas" && <ListsTab cfg={cfg} setCfg={setCfg} onSave={persist} saving={saving} />}
          {tab === "instituciones" && (
            <InstitutionsTab cfg={cfg} setCfg={setCfg} onSave={persist} saving={saving} onFeedback={setFeedback} />
          )}
          {tab === "almacenamiento" && <StorageTab cfg={cfg} setCfg={setCfg} onSave={persist} saving={saving} />}
        </>
      )}

      {dialog && (
        <DocDialog
          mode={dialog.mode}
          initial={dialog.doc}
          saving={saving}
          onClose={() => setDialog(null)}
          onSave={handleSaveDoc}
        />
      )}

      {profileDialog && (
        <ProfileDocDialog
          mode={profileDialog.mode}
          initial={profileDialog.doc}
          saving={saving}
          onClose={() => setProfileDialog(null)}
          onSave={handleSaveProfileDoc}
        />
      )}

      {postDialog && (
        <ApplicationDialog
          mode={postDialog.mode}
          initial={postDialog.post}
          stage1Docs={stage1Docs}
          saving={saving}
          onClose={() => setPostDialog(null)}
          onSave={handleSaveApplication}
        />
      )}
    </Box>
  );
}

const blankProfileDoc = () => ({ ...blankDoc(), boundTo: null, protected: false, stage: undefined });

// Documentos de la PERSONA. Los dos atados (formación / experiencia) son la
// misma decisión que la sección que los pide: apagar el documento apaga la
// sección en el formulario público.
function ProfileDocumentsTab({ catalog, onCreate, onEdit, onDelete, onToggle, saving }) {
  const bound = catalog.filter((d) => d.boundTo);
  const loose = catalog.filter((d) => !d.boundTo);
  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Documentos del aspirante</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={onCreate}>Nuevo documento</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pertenecen a la <strong>persona</strong>, no a una convocatoria: se cargan una vez y sirven
        para todas sus postulaciones.
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Secciones de la hoja de vida</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cada una es una lista repetible y su documento se adjunta <strong>dentro de cada
        registro</strong>. Apagar el interruptor oculta la sección completa del formulario público
        (lo ya registrado se conserva y el gestor lo sigue viendo). Un registro marcado «en curso» o
        «actualmente» no exige soporte.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {bound.map((d) => (
          <Grid item xs={12} md={6} key={d.key}>
            <Card variant="outlined" sx={{ height: "100%", opacity: d.enabled === false ? 0.55 : 1 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {PROFILE_SECTION_LABELS[d.boundTo] || d.boundTo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Soporte: {d.label || d.key} · {d.key}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center">
                    <Tooltip title={d.enabled === false ? "Sección desactivada" : "Sección activa"}>
                      <Switch size="small" checked={d.enabled !== false} disabled={saving}
                        onChange={(e) => onToggle(d, e.target.checked)} />
                    </Tooltip>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Atado a una sección: no se puede eliminar">
                      <span><IconButton size="small" disabled><LockIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                  <Chip size="small" color={d.required === false ? "default" : "primary"}
                    label={d.required === false ? "Soporte opcional" : "Soporte obligatorio"} />
                  <Chip size="small" variant="outlined"
                    label={Number(d.maxFiles) === 0 ? "Archivos ilimitados por registro" : `Hasta ${d.maxFiles || 1} archivo(s) por registro`} />
                  {d.enabled === false && <Chip size="small" label="Sección oculta" />}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Otros documentos de la persona</Typography>
      {loose.length === 0 ? (
        <Alert severity="info">
          Sin documentos personales sueltos. Crea uno para pedir, por ejemplo, la cédula.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {loose.map((d) => (
            <Grid item xs={12} md={6} key={d.key}>
              <Card variant="outlined" sx={{ height: "100%", opacity: d.enabled === false ? 0.55 : 1 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{d.label || d.key}</Typography>
                      <Typography variant="caption" color="text.secondary">{d.key}</Typography>
                    </Box>
                    <Box>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" onClick={() => onDelete(d)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                    <Chip size="small" color={d.required === false ? "default" : "primary"} label={d.required === false ? "Opcional" : "Obligatorio"} />
                    <Chip size="small" variant="outlined" label={Number(d.maxFiles) === 0 ? "Archivos ilimitados" : `Hasta ${d.maxFiles || 1} archivo(s)`} />
                    {d.enabled === false && <Chip size="small" label="Deshabilitado" />}
                  </Stack>
                  {d.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{d.description}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}

function ProfileDocDialog({ mode, initial, saving, onClose, onSave }) {
  const [d, setD] = useState({ ...blankProfileDoc(), ...initial });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const isBound = !!d.boundTo;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "create" ? "Nuevo documento del aspirante" : "Editar documento del aspirante"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isBound && (
            <Alert severity="info">
              Soporte de <strong>{PROFILE_SECTION_LABELS[d.boundTo] || d.boundTo}</strong>. Se adjunta
              dentro de cada registro y no puede re-apuntarse a otra sección.
            </Alert>
          )}
          <TextField label="Clave (key)" value={d.key} onChange={(e) => set("key", e.target.value.trim())}
            fullWidth disabled={!!d.protected}
            helperText="Identificador estable, sin espacios (p. ej. identity_document)." />
          <TextField label="Etiqueta (visible)" value={d.label} onChange={(e) => set("label", e.target.value)} fullWidth />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField type="number" fullWidth label="Orden" value={d.order ?? 0}
                onChange={(e) => set("order", Number(e.target.value))} inputProps={{ step: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={Number(d.maxFiles) === 0} onChange={(e) => set("maxFiles", e.target.checked ? 0 : 1)} />}
                label="Ilimitados"
              />
              {Number(d.maxFiles) !== 0 && (
                <TextField type="number" fullWidth label="Máx. archivos" value={d.maxFiles ?? 1}
                  onChange={(e) => set("maxFiles", Math.max(1, Number(e.target.value)))} inputProps={{ min: 1, step: 1 }}
                  helperText={isBound ? "Por cada registro de la sección." : "En total."} />
              )}
            </Grid>
          </Grid>
          <TextField label="Tipos MIME aceptados" value={(d.acceptedMime || []).join(", ")}
            onChange={(e) => set("acceptedMime", toList(e.target.value))} fullWidth
            helperText="Separados por coma. P. ej. application/pdf, image/*" />
          <TextField label="Descripción (contexto humano + IA)" value={d.description} onChange={(e) => set("description", e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="Pistas para IA" value={d.aiHints} onChange={(e) => set("aiHints", e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="Palabras clave" value={(d.keywords || []).join(", ")}
            onChange={(e) => set("keywords", toList(e.target.value))} fullWidth />
          <Stack direction="row" spacing={2}>
            <FormControlLabel control={<Checkbox checked={d.required !== false} onChange={(e) => set("required", e.target.checked)} />} label="Obligatorio" />
            <FormControlLabel control={<Switch checked={d.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} />}
              label={isBound ? "Sección activa" : "Habilitado"} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSave(d)} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DocumentsTab({ catalog, onCreate, onEdit, onDelete }) {
  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Documentos por convocatoria</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={onCreate}>Nuevo documento</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Se cargan <strong>en cada postulación</strong>. La etapa 1 es lo que pide la convocatoria al
        postularse; la etapa 2, lo que se le solicita al aspirante después de seleccionarlo. Los
        documentos de la persona (cédula y los soportes de formación y experiencia) viven en la
        pestaña <em>Documentos del aspirante</em>.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        La <strong>descripción</strong>, las <strong>palabras clave</strong> y las
        <strong> pistas para IA</strong> alimentan la futura clasificación / pre-chequeo automático.
      </Typography>

      {catalog.length === 0 ? (
        <Alert severity="info">Aún no hay documentos. Crea al menos uno.</Alert>
      ) : (
        <Grid container spacing={2}>
          {catalog.map((d) => {
            return (
              <Grid item xs={12} md={6} key={d.key}>
                <Card variant="outlined" sx={{ height: "100%", opacity: d.enabled === false ? 0.55 : 1 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{d.label || d.key}</Typography>
                        <Typography variant="caption" color="text.secondary">{d.key}</Typography>
                      </Box>
                      <Box>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" onClick={() => onDelete(d.key)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                      <Chip size="small" label={`Etapa ${d.stage || 1}`} />
                      <Chip size="small" color={d.required === false ? "default" : "primary"} label={d.required === false ? "Opcional" : "Obligatorio"} />
                      <Chip size="small" variant="outlined" label={Number(d.maxFiles) === 0 ? "Archivos ilimitados" : `Hasta ${d.maxFiles || 1} archivo(s)`} />
                      {d.enabled === false && <Chip size="small" label="Deshabilitado" />}
                    </Stack>
                    {d.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{d.description}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );
}

function DocDialog({ mode, initial, saving, onClose, onSave }) {
  const [d, setD] = useState({ ...blankDoc(), ...initial });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "create" ? "Nuevo documento" : "Editar documento"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Clave (key)" value={d.key} onChange={(e) => set("key", e.target.value.trim())}
            fullWidth helperText="Identificador estable, sin espacios (p. ej. acta_grado). Se guarda como type del documento." />
          <TextField label="Etiqueta (visible)" value={d.label} onChange={(e) => set("label", e.target.value)} fullWidth />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select fullWidth label="Etapa" value={d.stage || 1} onChange={(e) => set("stage", Number(e.target.value))}>
                <MenuItem value={1}>1 — Postulación</MenuItem>
                <MenuItem value={2}>2 — Post-selección</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField type="number" fullWidth label="Orden" value={d.order ?? 0} onChange={(e) => set("order", Number(e.target.value))} inputProps={{ step: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField type="number" fullWidth label="Mín. archivos" value={d.minFiles ?? 1} onChange={(e) => set("minFiles", Math.max(0, Number(e.target.value)))} inputProps={{ min: 0, step: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={Number(d.maxFiles) === 0} onChange={(e) => set("maxFiles", e.target.checked ? 0 : 1)} />}
                label="Ilimitados"
              />
              {Number(d.maxFiles) !== 0 && (
                <TextField type="number" fullWidth label="Máx. archivos" value={d.maxFiles ?? 1} onChange={(e) => set("maxFiles", Math.max(1, Number(e.target.value)))} inputProps={{ min: 1, step: 1 }}
                  helperText="Cuántos archivos de este tipo se pueden subir." />
              )}
            </Grid>
          </Grid>
          <TextField label="Tipos MIME aceptados" value={(d.acceptedMime || []).join(", ")}
            onChange={(e) => set("acceptedMime", toList(e.target.value))} fullWidth
            helperText="Separados por coma. P. ej. application/pdf, image/*" />
          <TextField label="Descripción (contexto humano + IA)" value={d.description} onChange={(e) => set("description", e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="Pistas para IA" value={d.aiHints} onChange={(e) => set("aiHints", e.target.value)} fullWidth multiline minRows={2}
            helperText="Qué debería contener el documento / cómo validarlo automáticamente." />
          <TextField label="Palabras clave" value={(d.keywords || []).join(", ")}
            onChange={(e) => set("keywords", toList(e.target.value))} fullWidth
            helperText="Sinónimos / frases esperadas, separadas por coma." />
          <Stack direction="row" spacing={2}>
            <FormControlLabel control={<Checkbox checked={d.required !== false} onChange={(e) => set("required", e.target.checked)} />} label="Obligatorio" />
            <FormControlLabel control={<Switch checked={d.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} />} label="Habilitado" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSave(d)} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ApplicationsTab({ applications, stage1Docs, onCreate, onEdit, onDelete }) {
  const labelOf = (key) => stage1Docs.find((d) => d.key === key)?.label || key;
  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Convocatorias</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={onCreate}>Nueva convocatoria</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Todo aspirante entra a <strong>General</strong> al registrarse. Crea convocatorias
        adicionales y define qué documentos de <strong>etapa 1</strong> requiere cada una; los
        documentos que el aspirante ya subió se reutilizan al aplicar.
      </Typography>

      <Grid container spacing={2}>
        {applications.map((p) => {
          const isGeneral = p.id === "general" || p.protected;
          const reqs = (p.requiredDocsStage1 || []).filter(Boolean);
          return (
            <Grid item xs={12} md={6} key={p.id}>
              <Card variant="outlined" sx={{ height: "100%", opacity: p.active === false ? 0.55 : 1 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={600}>{p.name || p.id}</Typography>
                        {isGeneral && <Tooltip title="Convocatoria permanente"><LockIcon fontSize="inherit" color="disabled" /></Tooltip>}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{p.id}</Typography>
                    </Box>
                    <Box>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={isGeneral ? "No se puede eliminar" : "Eliminar"}>
                        <span>
                          <IconButton size="small" disabled={isGeneral} onClick={() => onDelete(p)}><DeleteIcon fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                    <Chip size="small" color={p.active === false ? "default" : "success"} label={p.active === false ? "Inactiva" : "Activa"} />
                    <Chip size="small" variant="outlined"
                      label={reqs.length ? `${reqs.length} doc(s) etapa 1` : "Etapa 1 estándar"} />
                  </Stack>
                  {reqs.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {reqs.map(labelOf).join(", ")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

function ApplicationDialog({ mode, initial, stage1Docs, saving, onClose, onSave }) {
  const [p, setP] = useState({ ...blankApplication(), ...initial });
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const isGeneral = p.id === "general" || p.protected;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "create" ? "Nueva convocatoria" : "Editar convocatoria"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Nombre" value={p.name} onChange={(e) => set("name", e.target.value)} fullWidth
            helperText={isGeneral ? "Convocatoria permanente." : "Se genera un id estable a partir del nombre."} />
          <FormControl fullWidth>
            <InputLabel id="req-docs-label">Documentos requeridos (etapa 1)</InputLabel>
            <Select
              labelId="req-docs-label"
              multiple
              value={(p.requiredDocsStage1 || []).filter((k) => stage1Docs.some((d) => d.key === k))}
              onChange={(e) => set("requiredDocsStage1", typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
              input={<OutlinedInput label="Documentos requeridos (etapa 1)" />}
              renderValue={(selected) =>
                selected.map((k) => stage1Docs.find((d) => d.key === k)?.label || k).join(", ")}
            >
              {stage1Docs.map((d) => (
                <MenuItem key={d.key} value={d.key}>
                  <Checkbox checked={(p.requiredDocsStage1 || []).indexOf(d.key) > -1} />
                  <ListItemText primary={d.label || d.key} secondary={d.key} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Vacío = usa el conjunto estándar de etapa 1 del catálogo.
            </Typography>
          </FormControl>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={isGeneral ? true : p.active !== false} disabled={isGeneral}
                onChange={(e) => set("active", e.target.checked)} />}
              label="Activa" />
            <TextField type="number" label="Orden" value={p.order ?? 0}
              onChange={(e) => set("order", Number(e.target.value))} inputProps={{ step: 1 }} sx={{ width: 120 }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSave(p)} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ListsTab({ cfg, setCfg, onSave, saving }) {
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Listas y límites</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LinesField label="Tipos de documento de identidad" value={cfg.idTypes}
              onChange={(list) => set("idTypes", list)} fullWidth minRows={4}
              helperText="Uno por línea (Cédula de ciudadanía, Pasaporte…)." />
          </Grid>
          <Grid item xs={12} md={6}>
            <LinesField label="Niveles académicos" value={cfg.academicLevels}
              onChange={(list) => set("academicLevels", list)} fullWidth minRows={4}
              helperText={`Uno por línea (Técnico, Profesional…). ${FIXED_OPEN_LEVELS.join(", ")} son fijos: siempre aparecen aunque no estén aquí.`} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField type="number" fullWidth label="Tamaño máx. por archivo (MB)"
              value={Math.round((Number(cfg.maxFileBytes) || 0) / 1024 / 1024) || 10}
              onChange={(e) => set("maxFileBytes", (Number(e.target.value) || 10) * 1024 * 1024)}
              inputProps={{ min: 1, step: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!cfg.smsTestMode} onChange={(e) => set("smsTestMode", e.target.checked)} color="warning" />}
              label="Modo de prueba SMS" />
            <Typography variant="caption" color={cfg.smsTestMode ? "warning.main" : "text.secondary"} display="block">
              Solo para pruebas: usa los “números de teléfono para pruebas” de Firebase (código fijo, sin SMS ni reCAPTCHA).
              ⚠️ Mientras esté activo, los números reales fallan (invalid-app-credential). No dejarlo activo en producción.
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button variant="contained" disabled={saving}
            onClick={() => onSave({ idTypes: cfg.idTypes, academicLevels: cfg.academicLevels, maxFileBytes: cfg.maxFileBytes, smsTestMode: !!cfg.smsTestMode })}>
            {saving ? "Guardando…" : "Guardar listas"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// Closed catalog of educational institutions. An admin loads it from a CSV (or a
// one-name-per-line text file); the formación editor then only accepts names
// from this list. Leaving it empty restores free text.
function InstitutionsTab({ cfg, setCfg, onSave, saving, onFeedback }) {
  const [preview, setPreview] = useState(null); // { names, skipped, fileName }
  const [importing, setImporting] = useState(false);
  const current = institutionOptions(cfg);

  const handleFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const { names, skipped } = parseInstitutionsCsv(await file.text());
      if (names.length === 0) {
        onFeedback({ severity: "warning", msg: "El archivo no contiene ninguna institución." });
        setPreview(null);
      } else {
        setPreview({ names, skipped, fileName: file.name });
      }
    } catch (e) {
      onFeedback({ severity: "error", msg: "No fue posible leer el archivo." });
    } finally {
      setImporting(false);
    }
  };

  // The import REPLACES the catalog: the CSV published by the entity is the
  // authoritative list, so merging would keep names it deliberately dropped.
  const applyImport = async () => {
    if (!preview) return;
    setCfg((c) => ({ ...c, institutions: preview.names }));
    if (await onSave({ institutions: preview.names })) setPreview(null);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Instituciones educativas</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lista cerrada que el aspirante puede elegir en su formación académica. Cárgala desde un
          archivo CSV (una institución por fila; si tiene encabezado se usa la columna
          <em> institucion</em> / <em>nombre</em>). Si la lista queda vacía, el aspirante escribe el
          nombre libremente. Los títulos obtenidos en el exterior siempre permiten texto libre.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={saving || importing}>
            {importing ? "Leyendo…" : "Cargar CSV"}
            <input hidden type="file" accept=".csv,text/csv,text/plain"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
          </Button>
          <Chip label={`${current.length} instituciones en la lista`} size="small" />
        </Stack>

        {preview && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setPreview(null)} disabled={saving}>Descartar</Button>
                <Button size="small" variant="contained" onClick={applyImport} disabled={saving}>
                  {saving ? "Guardando…" : "Reemplazar lista"}
                </Button>
              </Stack>
            }
          >
            <strong>{preview.fileName}</strong>: {preview.names.length} instituciones listas para importar
            {preview.skipped > 0 ? ` (${preview.skipped} fila(s) sin nombre, omitidas)` : ""}.
            Esto <strong>reemplaza</strong> la lista actual de {current.length}.
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {preview.names.slice(0, 5).join(" · ")}{preview.names.length > 5 ? " …" : ""}
            </Typography>
          </Alert>
        )}

        <LinesField
          label="Instituciones" value={cfg.institutions}
          onChange={(list) => setCfg((c) => ({ ...c, institutions: list }))}
          fullWidth minRows={10} maxRows={20}
          helperText="Una por línea. Puedes editarla a mano después de importar el CSV." />

        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button variant="contained" disabled={saving} onClick={() => onSave({ institutions: cfg.institutions || [] })}>
            {saving ? "Guardando…" : "Guardar instituciones"}
          </Button>
        </Box>

        <OpenInstitutionsSection cfg={cfg} setCfg={setCfg} onSave={onSave} saving={saving} />
      </CardContent>
    </Card>
  );
}

// Institutions of the OPEN levels: colegios (primaria / secundaria) and the
// entities behind diplomados / certificaciones. Nobody uploads this list: it
// grows with each name an applicant registers, since none of them are in the
// catalog above. Here an ADMIN reviews it, removes junk or duplicates, and can
// open more levels on top of the fixed ones.
const OPEN_INSTITUTIONS_SHOWN = 200;

function OpenInstitutionsSection({ cfg, setCfg, onSave, saving }) {
  const [query, setQuery] = useState("");
  const names = openInstitutionOptions(cfg);
  const levels = academicLevelsOf(cfg);
  const openLevels = openLevelsOf(cfg);
  const q = query.trim().toLowerCase();
  const shown = q ? names.filter((n) => n.toLowerCase().includes(q)) : names;

  const toggleLevel = (level, on) => setCfg((c) => {
    const extra = (Array.isArray(c.openLevels) ? c.openLevels : []).filter((l) => l !== level);
    return { ...c, openLevels: on ? [...extra, level] : extra };
  });
  const removeName = (name) => setCfg((c) => ({ ...c, openInstitutions: (c.openInstitutions || []).filter((n) => n !== name) }));

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h6" gutterBottom>Colegios y otras instituciones</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Los colegios (primaria y secundaria) y las entidades que otorgan diplomados o
        certificaciones no están en la lista de instituciones de educación superior. En esos
        niveles el aspirante escribe el nombre (o lo elige si ya existe) y, si no estaba, se agrega
        a esta lista al guardar. Aquí puedes revisarla y quitar nombres mal escritos o repetidos.
      </Typography>

      <Typography variant="subtitle2">Niveles con institución de texto libre</Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        {levels.map((l) => {
          const fixed = FIXED_OPEN_LEVELS.includes(l);
          return (
            <FormControlLabel
              key={l}
              control={<Checkbox size="small" checked={openLevels.includes(l)} disabled={fixed || saving}
                onChange={(e) => toggleLevel(l, e.target.checked)} />}
              label={fixed ? `${l} (fijo)` : l}
            />
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {FIXED_OPEN_LEVELS.join(", ")} siempre existen y siempre son de texto libre.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <TextField size="small" label="Buscar" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: 260 }} />
        <Chip size="small" label={`${names.length} en la lista`} />
      </Stack>
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, maxHeight: 360, overflow: "auto" }}>
        {shown.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {names.length === 0 ? "Todavía ningún aspirante ha registrado un colegio o entidad." : "Ningún nombre coincide con la búsqueda."}
          </Typography>
        ) : shown.slice(0, OPEN_INSTITUTIONS_SHOWN).map((name) => (
          <Stack key={name} direction="row" alignItems="center" sx={{ px: 1.5, py: 0.25, "&:hover": { bgcolor: "action.hover" } }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{name}</Typography>
            <Tooltip title="Quitar de la lista">
              <IconButton size="small" aria-label={`Quitar ${name}`} onClick={() => removeName(name)} disabled={saving}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Box>
      {shown.length > OPEN_INSTITUTIONS_SHOWN && (
        <Typography variant="caption" color="text.secondary">
          Mostrando {OPEN_INSTITUTIONS_SHOWN} de {shown.length}. Busca para ver los demás.
        </Typography>
      )}
      <Box sx={{ mt: 2, textAlign: "right" }}>
        <Button variant="contained" disabled={saving}
          onClick={() => onSave({ openInstitutions: cfg.openInstitutions || [], openLevels: cfg.openLevels || [] })}>
          {saving ? "Guardando…" : "Guardar lista"}
        </Button>
      </Box>
    </Box>
  );
}

function StorageTab({ cfg, setCfg, onSave, saving }) {
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setDai = (k, v) => setCfg((c) => ({ ...c, documentAI: { ...(c.documentAI || {}), [k]: v } }));
  const dai = cfg.documentAI || {};
  const setXai = (k, v) => setCfg((c) => ({ ...c, experienceAI: { ...(c.experienceAI || {}), [k]: v } }));
  const xai = cfg.experienceAI || {};
  const setEai = (k, v) => setCfg((c) => ({ ...c, educationAI: { ...(c.educationAI || {}), [k]: v } }));
  const eai = cfg.educationAI || {};
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Almacenamiento y OCR</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TextField label="Bucket de almacenamiento (GCS)" value={cfg.storageBucket || ""}
              onChange={(e) => set("storageBucket", e.target.value.trim())} fullWidth
              helperText="Destino donde se guardan los documentos de aspirantes. Fuente de verdad para el backend." />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Document AI (OCR)</Typography>
            <Typography variant="caption" color="text.secondary">
              El projectId se resuelve automáticamente en tiempo de ejecución
              ({process.env.REACT_APP_PROJECT_ID || "runtime"}). Deja ubicación/procesador en blanco
              para usar los valores por defecto.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Ubicación (location)" value={dai.location || ""} onChange={(e) => setDai("location", e.target.value.trim())} fullWidth placeholder="us" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Procesador (processorId)" value={dai.processorId || ""} onChange={(e) => setDai("processorId", e.target.value.trim())} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Lectura con IA del certificado de experiencia</Typography>
            <Typography variant="caption" color="text.secondary">
              Al adjuntar un certificado laboral, se lee con OCR y la IA propone la empresa, el NIT y los
              periodos para que el aspirante solo los revise.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={xai.enabled !== false} onChange={(e) => setXai("enabled", e.target.checked)} />}
              label="Habilitada"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Modelo" value={xai.model || ""} placeholder="gpt-4o-mini"
              onChange={(e) => setXai("model", e.target.value.trim())} fullWidth
              helperText="En blanco usa gpt-4o-mini." />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Lectura con IA del acta de grado / diploma</Typography>
            <Typography variant="caption" color="text.secondary">
              Al adjuntar el acta de grado y/o el diploma, la IA propone el nivel, el título, la
              institución (buscada en el catálogo) y la fecha de grado para que el aspirante solo los revise.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={eai.enabled !== false} onChange={(e) => setEai("enabled", e.target.checked)} />}
              label="Habilitada"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Modelo" value={eai.model || ""} placeholder="gpt-4o-mini"
              onChange={(e) => setEai("model", e.target.value.trim())} fullWidth
              helperText="En blanco usa gpt-4o-mini." />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Límite de lecturas con IA</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Límite por IP cada 10 minutos" type="number" inputProps={{ min: 1 }}
              value={xai.rateLimitPerIp ?? 50}
              onChange={(e) => setXai("rateLimitPerIp", Math.max(1, Math.floor(Number(e.target.value) || 50)))}
              fullWidth
              helperText="Lectura de certificados de experiencia y formación (cada una cuenta por separado). Varios aspirantes desde una misma red comparten IP." />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button variant="contained" disabled={saving}
            onClick={() => onSave({ storageBucket: cfg.storageBucket, documentAI: cfg.documentAI, experienceAI: cfg.experienceAI, educationAI: cfg.educationAI })}>
            {saving ? "Guardando…" : "Guardar almacenamiento"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
